/**
 * Invalidação explícita de owner anterior após failover/promoção.
 */

const LS_KEY = 'GOMARKE_INVALID_OWNER_V1';

export interface InvalidOwnerRecord {
  tabId: string;
  epoch: number;
  fencingToken: number;
  invalidatedAt: number;
}

const memory = new Map<string, InvalidOwnerRecord>();
const invalidatedOperationTabs = new Set<string>();

function loadLs(): Record<string, InvalidOwnerRecord> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, InvalidOwnerRecord>;
  } catch {
    return {};
  }
}

function persistLs(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(memory)));
  } catch {
    // quota
  }
}

/** Invalida flush/replay/recovery em andamento do tab perdedor. */
export function invalidateActiveOperationsForTab(tabId: string): void {
  invalidatedOperationTabs.add(tabId);
}

export function isActiveOperationsInvalidated(tabId: string): boolean {
  return invalidatedOperationTabs.has(tabId);
}

export function clearActiveOperationsInvalidation(tabId: string): void {
  invalidatedOperationTabs.delete(tabId);
}

export function invalidatePreviousOwner(
  tabId: string,
  epoch: number,
  fencingToken: number
): void {
  invalidateActiveOperationsForTab(tabId);
  const rec: InvalidOwnerRecord = {
    tabId,
    epoch,
    fencingToken,
    invalidatedAt: Date.now(),
  };
  memory.set(tabId, rec);
  persistLs();
}

export function isOwnerInvalidated(
  tabId: string,
  epoch: number,
  fencingToken: number
): boolean {
  const rec = memory.get(tabId) ?? loadLs()[tabId];
  if (!rec) return false;
  if (epoch > rec.epoch) return false;
  if (epoch < rec.epoch) return false;
  return fencingToken <= rec.fencingToken;
}

export function clearInvalidationOnPromote(tabId: string): void {
  clearActiveOperationsInvalidation(tabId);
  memory.delete(tabId);
  const all = loadLs();
  delete all[tabId];
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(all));
    } catch {
      // ignore
    }
  }
}

export function hydrateInvalidationsFromLs(): void {
  for (const [k, v] of Object.entries(loadLs())) {
    memory.set(k, v);
  }
}

export function _testClearInvalidations(): void {
  memory.clear();
  invalidatedOperationTabs.clear();
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LS_KEY);
  }
}
